import {
  AnalyzerNodeData,
  UpdateWorkflowNodeRequest,
  WorkflowNodeType,
  WorkflowsService,
} from '@/api';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { useStore } from '@nanostores/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNodes } from '@xyflow/react';
import { toast } from 'sonner';
import { FormattedNode } from '../../WorkflowBuilder';
import AgentsSelector from '../AgentsSelector';

export default function AnalyzerConfiguration({
  selectedNode,
  setNodes,
  readOnly = false,
}: {
  selectedNode: FormattedNode;
  setNodes: any;
  readOnly?: boolean;
}) {
  const nodes = useNodes();
  const wF = useStore($workflow);
  const queryClient = useQueryClient();

  const [selectedAgents, setSelectedAgents] = useState<Record<
    string,
    boolean | null
  > | null>({});

  function updateAgentsOnNode() {
    if (readOnly) return;
    // get the current selected node
    const newNodes = JSON.parse(JSON.stringify(nodes));

    const updatableNode = newNodes.find((n: any) => n.id === selectedNode.id);

    // find the index of selected node
    const selectedNodeIndex = newNodes.findIndex(
      (n: any) => n.id === selectedNode.id,
    );

    if (updatableNode) {
      // update the node data
      updatableNode.data = {
        ...updatableNode.data,
        agents: selectedAgents,

        type: 'ANALYZER' as WorkflowNodeType,
      };
      // set nodes
      newNodes[selectedNodeIndex] = updatableNode;

      setNodes(newNodes);
    }
  }

  // mutation to update a node
  const updateNode = useMutation({
    mutationFn: async (payload: {
      req: UpdateWorkflowNodeRequest;
      nodeId: string;
    }) => {
      // call the api to create a new workspace
      if (wF) {
        const res = await WorkflowsService.updateWorkflowNode(
          wF.id,
          payload.nodeId,
          payload.req,
        );
        return res;
      }
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries({
        queryKey: ['getWorkflow', wF?.id],
      });
      toast.success('Saved successfully!', {
        description: 'Your changes have been saved.',
        duration: 5000,
      });
    },
    onError: error => {
      console.error(error);

      // show a toast message
      toast.error('An error occurred :: Unable to add node.', {
        description: error.message,
        duration: 5000,
      });
    },
  });

  useEffect(() => {
    updateAgentsOnNode();
  }, [selectedAgents]);

  function isAnalyzerNodeData(data: any): data is AnalyzerNodeData {
    return data.type === 'ANALYZER';
  }

  useEffect(() => {
    setSelectedAgents({});
    if (isAnalyzerNodeData(selectedNode.data)) {
      setSelectedAgents(selectedNode?.data.agents);
    }
  }, [selectedNode]);

  return (
    <div className="h-full w-full overflow-y-auto">
      <Accordion
        type="single"
        collapsible
        className="w-full"
        defaultValue="item-1"
      >
        <AccordionItem
          value="item-1"
          className="rounded-md border-none bg-white pb-2 pl-4 pr-4 pt-2"
        >
          <AccordionTrigger className={`pb-1 pt-1 ${readOnly ? "text-gray-500 opacity-70 cursor-not-allowed" : ""}`}
            onClick={e => readOnly && e.preventDefault()}
          >
            Select Agents
          </AccordionTrigger>
          <AccordionContent>
            <div className={`h-[300px] overflow-auto ${readOnly ? "text-gray-500 opacity-70 cursor-not-allowed" : ""}`} >
              <AgentsSelector
                agents={selectedAgents}
                setAgents={readOnly ? () => { } : setSelectedAgents}

              />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="mt-2 flex justify-end">
        <Button
          variant="outline"
          size="sm"
          disabled={readOnly || updateNode.isPending} // ✅ Disable when readOnly
          onClick={async () => {
            if (!readOnly) { // ✅ Prevent saving in readOnly mode
              const updatableNode = nodes.find((n: any) => n.id === selectedNode.id);
              if (updatableNode) {
                await updateNode.mutateAsync({
                  req: { data: updatableNode.data as AnalyzerNodeData },
                  nodeId: updatableNode?.id,
                });
              }
            }
          }}
        >
          Save
        </Button>

      </div>
    </div>
  );
}
